import { useQuery } from '@zg-rentals/react-query5';
import {
  AnalyticsFilterSearchCategory,
  CustomerAnalyticsEnterpriseAnalyticsApiHandler_getRenterExpectations,
  CustomerAnalyticsEnterpriseAnalyticsApiHandler_getRenterExpectations_Path,
  DeprecatedZillowRegionType,
  type PortfolioAnalyticsPropertyResponseDto,
  RenterActivityStatsApiHandler_getFilterStatsByCategory,
  RenterActivityStatsApiHandler_getFilterStatsByCategory_Path,
  RenterActivityStatsApiHandler_getRenterAnnualIncomeDistribution,
  RenterActivityStatsApiHandler_getRenterAnnualIncomeDistribution_Path,
  RenterActivityStatsApiHandler_getRenterDemographicsMonthlyData,
  RenterActivityStatsApiHandler_getRenterDemographicsMonthlyData_Path,
  RenterActivityStatsApiHandler_getRenterMonthlyAffordableRentDistribution,
  RenterActivityStatsApiHandler_getRenterMonthlyAffordableRentDistribution_Path,
  RenterActivityStatsApiHandler_getTopCityMovingStats,
  RenterActivityStatsApiHandler_getTopCityMovingStats_Path,
  RenterActivityStatsApiHandler_getTopKeywordSearches,
  RenterActivityStatsApiHandler_getTopKeywordSearches_Path,
} from '@zg-rentals/ts-customer-analytics-client';
import { mockRenterData } from '../../../../stories/data/renterData';
import {
  aggregateFilterStats,
  aggregateKeywordStats,
  mapAffordableRentDistributionToQuartiles,
  mapAnnualIncomeDistribution,
  mapRenterExpectationsToRent,
  type PercentageDatum,
  type QuartileData,
  sortBedroomData,
} from './RenterDataReport.utils';
import { useMainLayout } from '../../../layouts/MainPageLayout/MainLayoutContext';
import { DisplayingRegionLevel } from '../../../../types/DisplayingRegionLevel';

type RenterData = {
  medianHouseholdIncome: { value: number | null; delta: number };
  population: { value: number | null; delta: number };
  percentRenters: { value: number | null; delta: number };
  rentersMovedFrom: Array<PercentageDatum>;
  rentersMovedTo: Array<PercentageDatum>;
  estimatedAffordableRent: Array<PercentageDatum>;
  annualIncome: Array<PercentageDatum>;
  petOwnership: Array<PercentageDatum>;
  bathroomsSearched: Array<PercentageDatum>;
  bedroomsSearched: Array<PercentageDatum>;
  topAmenitySearches: Array<PercentageDatum>;
  topKeywordSearches: Array<PercentageDatum>;
  rentQuartiles: Array<QuartileData> | null;
  isLoadingAffordableRent: boolean;
  isLoadingDemographics: boolean;
  isLoadingPets: boolean;
  isLoadingBedrooms: boolean;
  isLoadingBathrooms: boolean;
  isLoadingAmenities: boolean;
  isLoadingKeywords: boolean;
  isLoadingExpectations: boolean;
  isLoadingCityMoving: boolean;
};

export const regionIdTypeToZillowRegionType = (
  regionIdType: DisplayingRegionLevel,
  selectedMarketId: number,
  selectedProperty: PortfolioAnalyticsPropertyResponseDto | undefined,
) => {
  if (selectedProperty) {
    switch (true) {
      case selectedMarketId === selectedProperty.propertyDetails.cityRegionId:
        return DeprecatedZillowRegionType.City;
      case selectedMarketId === selectedProperty.propertyDetails.metroStatRegionId:
        return DeprecatedZillowRegionType.CoreBasedStatArea;
      case selectedMarketId === selectedProperty.propertyDetails.stateRegionId:
        return DeprecatedZillowRegionType.State;
    }
  }

  switch (regionIdType) {
    case DisplayingRegionLevel.STATE:
      return DeprecatedZillowRegionType.State;
    case DisplayingRegionLevel.METRO_AREA:
      return DeprecatedZillowRegionType.CoreBasedStatArea;
    case DisplayingRegionLevel.CITY:
      return DeprecatedZillowRegionType.City;
    case DisplayingRegionLevel.ZIP:
      return DeprecatedZillowRegionType.ZipCode;
    default:
      return DeprecatedZillowRegionType.State;
  }
};

export const useRenterData = (isNationwide: boolean, showMockDataSections = false): RenterData => {
  const { selectedMarketId, selectedDisplayingRegionLevel, allProperties, selectedFilters } = useMainLayout();

  const selectedProperty = allProperties.find(
    (property) => property.propertyDetails.listingAlias === selectedFilters.listingAlias,
  );

  const zillowRegionIds = [selectedMarketId];
  const regionType = regionIdTypeToZillowRegionType(selectedDisplayingRegionLevel, selectedMarketId, selectedProperty);

  const { data: bathroomsData, isLoading: isLoadingBathrooms } = useQuery({
    queryKey: [
      RenterActivityStatsApiHandler_getFilterStatsByCategory_Path,
      AnalyticsFilterSearchCategory.BATHROOM,
      zillowRegionIds,
    ],
    queryFn: () =>
      RenterActivityStatsApiHandler_getFilterStatsByCategory({
        category: AnalyticsFilterSearchCategory.BATHROOM,
        zillowRegionIds,
      }),
  });

  const { data: bedroomsData, isLoading: isLoadingBedrooms } = useQuery({
    queryKey: [
      RenterActivityStatsApiHandler_getFilterStatsByCategory_Path,
      AnalyticsFilterSearchCategory.BEDROOM,
      zillowRegionIds,
    ],
    queryFn: () =>
      RenterActivityStatsApiHandler_getFilterStatsByCategory({
        category: AnalyticsFilterSearchCategory.BEDROOM,
        zillowRegionIds,
      }),
  });

  const { data: amenitiesData, isLoading: isLoadingAmenities } = useQuery({
    queryKey: [
      RenterActivityStatsApiHandler_getFilterStatsByCategory_Path,
      AnalyticsFilterSearchCategory.AMENITY,
      zillowRegionIds,
    ],
    queryFn: () =>
      RenterActivityStatsApiHandler_getFilterStatsByCategory({
        category: AnalyticsFilterSearchCategory.AMENITY,
        zillowRegionIds,
        limit: 5,
      }),
  });

  const { data: petsData, isLoading: isLoadingPets } = useQuery({
    queryKey: [
      RenterActivityStatsApiHandler_getFilterStatsByCategory_Path,
      AnalyticsFilterSearchCategory.PETS,
      zillowRegionIds,
    ],
    queryFn: () =>
      RenterActivityStatsApiHandler_getFilterStatsByCategory({
        category: AnalyticsFilterSearchCategory.PETS,
        zillowRegionIds,
      }),
  });

  const { data: keywordsData, isLoading: isLoadingKeywords } = useQuery({
    queryKey: [RenterActivityStatsApiHandler_getTopKeywordSearches_Path, zillowRegionIds],
    queryFn: () =>
      RenterActivityStatsApiHandler_getTopKeywordSearches({
        zillowRegionIds,
        limit: 5,
      }),
  });

  const { data: renterExpectationsData, isLoading: isLoadingExpectations } = useQuery({
    queryKey: [CustomerAnalyticsEnterpriseAnalyticsApiHandler_getRenterExpectations_Path, selectedMarketId],
    queryFn: () =>
      CustomerAnalyticsEnterpriseAnalyticsApiHandler_getRenterExpectations({
        areaId: String(selectedMarketId),
      }),
  });

  const { data: annualIncomeData, isLoading: isLoadingAnnualIncome } = useQuery({
    queryKey: [RenterActivityStatsApiHandler_getRenterAnnualIncomeDistribution_Path, selectedMarketId],
    queryFn: () =>
      RenterActivityStatsApiHandler_getRenterAnnualIncomeDistribution({
        zillowRegionId: selectedMarketId,
      }),
  });

  const { data: renterDemographicsData, isLoading: isLoadingDemographics } = useQuery({
    queryKey: [RenterActivityStatsApiHandler_getRenterDemographicsMonthlyData_Path, selectedMarketId],
    queryFn: () =>
      RenterActivityStatsApiHandler_getRenterDemographicsMonthlyData({
        zillowRegionId: selectedMarketId,
      }),
  });

  const { data: affordableRentData, isLoading: isLoadingAffordableRent } = useQuery({
    queryKey: [RenterActivityStatsApiHandler_getRenterMonthlyAffordableRentDistribution_Path, selectedMarketId],
    queryFn: () =>
      RenterActivityStatsApiHandler_getRenterMonthlyAffordableRentDistribution({
        zillowRegionId: selectedMarketId,
      }),
  });

  const { data: topCityMovingData, isLoading: isLoadingCityMoving } = useQuery({
    queryKey: [RenterActivityStatsApiHandler_getTopCityMovingStats_Path, selectedMarketId],
    queryFn: () =>
      RenterActivityStatsApiHandler_getTopCityMovingStats({
        zillowRegionId: !isNationwide ? String(selectedMarketId) : undefined,
        zillowRegionType: !isNationwide ? regionType : undefined,
      }),
  });

  const medianHouseholdIncome = renterDemographicsData
    ? {
        value: Math.round(renterDemographicsData.medianMonthlyIncome * 12) || null,
        delta: Math.round(renterDemographicsData.medianMonthlyIncomeVariation * 12),
      }
    : { value: null, delta: 0 };

  const population = renterDemographicsData
    ? {
        value: Math.round(renterDemographicsData.totalPopulation) || null,
        delta: Math.round(renterDemographicsData.totalPopulationVariation),
      }
    : { value: null, delta: 0 };

  const percentRenters = renterDemographicsData
    ? {
        value: Math.round(renterDemographicsData.percentageWhoRent) || null,
        delta: Math.round(renterDemographicsData.percentageWhoRentVariation),
      }
    : { value: null, delta: 0 };

  return {
    medianHouseholdIncome,
    population,
    percentRenters,
    rentersMovedFrom:
      topCityMovingData?.rentersMovedFrom.map((c) => ({
        label: c.cityName,
        value: Math.round(c.percentage * 100),
      })) ?? [],
    rentersMovedTo:
      topCityMovingData?.rentersMovedTo.map((c) => ({
        label: c.cityName,
        value: Math.round(c.percentage * 100),
      })) ?? [],
    estimatedAffordableRent:
      renterExpectationsData && renterExpectationsData.hasMarketData
        ? mapRenterExpectationsToRent(renterExpectationsData)
        : showMockDataSections
          ? mockRenterData.estimatedAffordableRent
          : [],
    annualIncome: annualIncomeData
      ? mapAnnualIncomeDistribution(annualIncomeData)
      : showMockDataSections
        ? mockRenterData.annualIncome
        : [],
    petOwnership: petsData ? aggregateFilterStats(petsData) : mockRenterData.petOwnership,
    bathroomsSearched: bathroomsData ? aggregateFilterStats(bathroomsData) : mockRenterData.bathroomsSearched,
    bedroomsSearched: bedroomsData
      ? sortBedroomData(aggregateFilterStats(bedroomsData))
      : mockRenterData.bedroomsSearched,
    topAmenitySearches: amenitiesData ? aggregateFilterStats(amenitiesData) : mockRenterData.topAmenitySearches,
    topKeywordSearches: keywordsData ? aggregateKeywordStats(keywordsData) : mockRenterData.topKeywordSearches,
    rentQuartiles: affordableRentData ? mapAffordableRentDistributionToQuartiles(affordableRentData) : null,
    isLoadingAffordableRent,
    isLoadingDemographics,
    isLoadingPets,
    isLoadingBedrooms,
    isLoadingBathrooms,
    isLoadingAmenities,
    isLoadingKeywords,
    isLoadingExpectations: isLoadingExpectations || isLoadingAnnualIncome,
    isLoadingCityMoving,
  };
};
